import { Component, Input, OnInit, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { forkJoin } from 'rxjs';

import { UI, type UiBadgeVariant } from '../../components/ui';
import { DashboardTransactionChartsComponent } from './dashboard-transaction-charts.component';

import {
  lucideUsers,
  lucideStore,
  lucideBanknote,
  lucideBookOpen,
  lucideArrowLeftRight,
  lucideSettings,
  lucideTrendingUp,
  lucideTrendingDown,
  lucideTriangleAlert,
  lucideCircleCheck,
  lucideClock,
  lucideChartColumn,
  lucideChevronLeft,
  lucideChevronRight,
} from '@ng-icons/lucide';

import {
  DashboardService,
  type EntitySummary,
  type TransactionStats,
  type AlertsData,
  type DashboardKpiOverview,
  type DailyChartData,
  type RecentActivity,
  type RecentActivityItem,
  type WalletBalances,
  type StatusBreakdown,
  type StatusBreakdownItem,
  type TransactionPreviousPeriod,
  type TransactionTypeSummary,
  type TransactionSummary,
} from '../../services/dashboard.service';

import { type DashboardLayout, sectionFlagsForLayout } from './dashboard-layout';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { FeeAnalyticsService, type FeeSummaryResponse } from '../../services/fee-analytics.service';
import { formatHumanAmount } from '../../utils/admin-formatting';

type Period = 'today' | 'week' | 'month' | 'quarter' | 'halfyear' | 'year' | 'custom';
type MetricKey = keyof Pick<
  TransactionStats,
  'deposits' | 'payouts' | 'refunds' | 'remittances' | 'settlements' | 'batches'
>;

interface MetricCard {
  key: MetricKey;
  label: string;
  iconTone: string;
}

interface CurrencyVolumeEntry {
  code: string;
  title: string;
  symbol: string;
  amount: number;
  count: number;
}

const VOLUME_CURRENCY_SLOT_COUNT = 3;

/** Stable palette for transaction type horizontal bars (no random colors). */
const TYPE_BAR_COLORS = [
  'var(--c-info)',
  'var(--c-success)',
  'var(--c-warning)',
  'var(--c-accent)',
  'var(--c-brand)',
  'var(--c-danger)',
  'var(--c-muted)',
];

@Component({
  selector: 'app-dashboard-system',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, NgIcon, DashboardTransactionChartsComponent, ...UI],
  templateUrl: './dashboard-system.component.html',
  viewProviders: [
    provideIcons({
      lucideUsers,
      lucideStore,
      lucideBanknote,
      lucideBookOpen,
      lucideArrowLeftRight,
      lucideSettings,
      lucideTrendingUp,
      lucideTrendingDown,
      lucideTriangleAlert,
      lucideCircleCheck,
      lucideClock,
      lucideChartColumn,
      lucideChevronLeft,
      lucideChevronRight,
    }),
  ],
})
export class DashboardSystemComponent implements OnInit {
  private readonly dashboardService = inject(DashboardService);
  private readonly feeAnalyticsService = inject(FeeAnalyticsService);
  readonly shellRoutes = inject(ShellRoutesService);

  /** Dashboard heading (e.g. system vs merchant). */
  @Input() pageTitle = 'Dashboard';
  /** Subtitle under the main heading. */
  @Input() pageSubtitle = 'Business intelligence overview';

  /** Exact role layout: controls which widgets/sections render. */
  readonly dashboardLayout = input<DashboardLayout>('SYSTEM_ADMIN');

  readonly sectionFlags = computed(() => sectionFlagsForLayout(this.dashboardLayout()));

  readonly loading = signal(true);
  readonly selectedPeriod = signal<Period>('today');
  readonly startDate = signal<string | null>(null);
  readonly endDate = signal<string | null>(null);

  readonly summary = signal<EntitySummary | null>(null);
  readonly transactions = signal<TransactionStats | null>(null);
  readonly alerts = signal<AlertsData | null>(null);
  readonly kpiOverview = signal<DashboardKpiOverview | null>(null);
  readonly charts = signal<DailyChartData | null>(null);
  readonly recentActivity = signal<RecentActivity | null>(null);
  readonly walletBalances = signal<WalletBalances | null>(null);
  readonly statusBreakdown = signal<StatusBreakdown | null>(null);
  readonly transactionTypesSummary = signal<TransactionTypeSummary[]>([]);
  readonly feeSummary = signal<FeeSummaryResponse | null>(null);
  readonly metricCarouselIndex = signal(0);

  readonly periods: { key: Period; label: string }[] = [
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'Week' },
    { key: 'month', label: 'Month' },
    { key: 'quarter', label: '3 Months' },
    { key: 'halfyear', label: '6 Months' },
    { key: 'year', label: 'Year' },
    { key: 'custom', label: 'Custom' },
  ];

  private readonly metricCards: MetricCard[] = [
    { key: 'deposits', label: 'DEPOSIT', iconTone: 'green' },
    { key: 'payouts', label: 'PAYOUT', iconTone: 'blue' },
    { key: 'refunds', label: 'REFUND', iconTone: 'amber' },
    { key: 'remittances', label: 'REMITTANCE', iconTone: 'purple' },
    { key: 'settlements', label: 'SETTLEMENT', iconTone: 'indigo' },
    { key: 'batches', label: 'BATCH', iconTone: 'slate' },
  ];

  /** Active metric for volume hero carousel (signal-backed). */
  readonly activeMetricCard = computed((): MetricCard | null => {
    const cards = this.metricCards;
    if (!cards.length) return null;
    const total = cards.length;
    const index = ((this.metricCarouselIndex() % total) + total) % total;
    return cards[index] ?? null;
  });

  /** Always three slots (top currencies + zero placeholders) for stable layout. */
  readonly successfulVolumeEntries = computed((): CurrencyVolumeEntry[] => {
    const emptySlot = (): CurrencyVolumeEntry => ({
      code: '',
      title: '',
      symbol: '',
      amount: 0,
      count: 0,
    });

    const txData = this.transactions();
    const active = this.activeMetricCard();
    if (!txData || !active) {
      return Array.from({ length: VOLUME_CURRENCY_SLOT_COUNT }, emptySlot);
    }

    const bucket = txData[active.key];
    const meta = bucket.amount_period_currency_meta ?? {};
    const counts = bucket.count_period_by_currency ?? {};
    const sorted = Object.entries(bucket.amount_period_by_currency ?? {})
      .map(([code, amount]) => ({
        code,
        title: meta[code]?.title ?? code,
        symbol: meta[code]?.symbol ?? '',
        amount,
        count: counts[code] ?? 0,
      }))
      .sort((a, b) => b.amount - a.amount);

    const slots = sorted.slice(0, VOLUME_CURRENCY_SLOT_COUNT);
    while (slots.length < VOLUME_CURRENCY_SLOT_COUNT) {
      slots.push(emptySlot());
    }
    return slots;
  });

  ngOnInit(): void {
    this.loadAllData();
  }

  loadAllData(): void {
    this.loading.set(true);

    forkJoin({
      summary: this.dashboardService.getSummary(),
      wallets: this.dashboardService.getWalletBalances(),
      kpiOverview: this.dashboardService.getKpiOverview(),
    }).subscribe(({ summary, wallets, kpiOverview }) => {
      this.summary.set(summary);
      this.walletBalances.set(wallets);
      this.kpiOverview.set(kpiOverview);
      this.loadPeriodData();
    });
  }

  loadPeriodData(): void {
    this.loading.set(true);
    const period = this.selectedPeriod();
    const startDate = this.startDate();
    const endDate = this.endDate();
    const chartDays = this.getChartDays(period);
    const sd = startDate ?? undefined;
    const ed = endDate ?? undefined;

    const apiPeriod = this.getApiPeriod(period);

    forkJoin({
      transactions: this.dashboardService.getTransactions(apiPeriod, sd, ed),
      charts: this.dashboardService.getCharts(chartDays),
      types: this.dashboardService.getTransactionTypesSummary(apiPeriod, sd, ed),
      status: this.dashboardService.getStatusBreakdown(apiPeriod, sd, ed),
      recent: this.dashboardService.getRecentActivity(5, apiPeriod),
      alerts: this.dashboardService.getAlerts(apiPeriod, sd, ed),
      fees: this.feeAnalyticsService.getSummary({
        period: apiPeriod,
        start_date: sd ? `${sd}T00:00:00Z` : undefined,
        end_date: ed ? `${ed}T23:59:59Z` : undefined,
      }),
    }).subscribe(({ transactions, charts, types, status, recent, alerts, fees }) => {
      this.transactions.set(transactions);
      this.charts.set(charts);
      this.transactionTypesSummary.set(types);
      this.statusBreakdown.set(status);
      this.recentActivity.set(recent);
      this.alerts.set(alerts);
      this.feeSummary.set(fees);
      this.loading.set(false);
    });
  }

  onPeriodChange(period: Period, startDate?: string, endDate?: string): void {
    this.selectedPeriod.set(period);
    if (period === 'quarter') {
      const [qs, qe] = this.getRollingRange(90);
      this.startDate.set(qs);
      this.endDate.set(qe);
    } else if (period === 'halfyear') {
      const [hs, he] = this.getRollingRange(180);
      this.startDate.set(hs);
      this.endDate.set(he);
    } else {
      this.startDate.set(startDate || null);
      this.endDate.set(endDate || null);
    }
    this.loadPeriodData();
  }

  /**
   * Format currency amount.
   */
  formatAmount(amount: number | undefined): string {
    if (amount === undefined || amount === null) return '—';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  formatMoney(amount: string | undefined, currency: string): string {
    return formatHumanAmount(amount, currency);
  }

  formatCurrencyAmount(amount: number | undefined, currencyCode: string): string {
    if (amount === undefined || amount === null) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'code',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  /** Empty code = padded slot; avoid invalid ISO currency in Intl. */
  isVolumePlaceholder(entry: CurrencyVolumeEntry): boolean {
    return !entry.code?.trim();
  }

  formatVolumeAmount(entry: CurrencyVolumeEntry): string {
    if (this.isVolumePlaceholder(entry)) {
      return this.formatAmount(0);
    }
    return this.formatCurrencyAmount(entry.amount, entry.code);
  }

  volumeIconLabel(entry: CurrencyVolumeEntry): string {
    if (this.isVolumePlaceholder(entry)) return '—';
    return entry.symbol || entry.code.slice(0, 2).toUpperCase();
  }

  volumeTitleDisplay(entry: CurrencyVolumeEntry): string {
    if (this.isVolumePlaceholder(entry)) return '—';
    return entry.title;
  }

  /**
   * Format large numbers with K/M suffix.
   */
  formatCompact(value: number | undefined): string {
    if (value === undefined || value === null) return '—';
    if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
    if (value >= 1000) return (value / 1000).toFixed(1) + 'K';
    return value.toString();
  }

  getChartDays(period: Period): number {
    switch (period) {
      case 'today':
        return 1;
      case 'week':
        return 7;
      case 'month':
        return 30;
      case 'year':
        return 365;
      case 'quarter':
        return 90;
      case 'halfyear':
        return 180;
      case 'custom':
        return 30;
      default:
        return 14;
    }
  }

  /**
   * Sum counts for status breakdown rows (optional filter by status substring).
   */
  sumStatusCounts(items: StatusBreakdownItem[] | undefined): number {
    if (!items?.length) return 0;
    return items.reduce((acc, row) => acc + (row.count ?? 0), 0);
  }

  /**
   * Count for a status value (exact match, case-sensitive per API).
   */
  getStatusCount(items: StatusBreakdownItem[] | undefined, status: string): number {
    const row = items?.find((x) => x.status === status);
    return row?.count ?? 0;
  }

  /**
   * Pending + processing counts for queue-style badges.
   */
  getPendingPipelineCount(items: StatusBreakdownItem[] | undefined): number {
    return this.getStatusCount(items, 'PENDING') + this.getStatusCount(items, 'PROCESSING');
  }

  getSuccessfulCount(items: StatusBreakdownItem[] | undefined): number {
    return this.getStatusCount(items, 'SUCCESS') + this.getStatusCount(items, 'COMPLETED');
  }

  /**
   * Bar width % for transaction type row (share of total count).
   */
  getTypeBarPercent(value: number, total: number): number {
    if (total <= 0) return 0;
    return Math.max((value / total) * 100, 2);
  }

  /**
   * Total count across transaction type summary.
   */
  getTransactionTypesTotal(): number {
    const rows = this.transactionTypesSummary();
    return rows.reduce((acc, r) => acc + (r.value ?? 0), 0);
  }

  /**
   * Color for transaction type bar by index.
   */
  getTypeBarColor(index: number): string {
    return TYPE_BAR_COLORS[index % TYPE_BAR_COLORS.length];
  }

  getMetricCards(): MetricCard[] {
    return this.metricCards;
  }

  getKpiTrend(label: string): number | null {
    const overview = this.kpiOverview();
    if (!overview) return null;
    const row = overview.kpis.find((item) => item.label.toLowerCase() === label.toLowerCase());
    return row?.trend_percent ?? null;
  }

  nextMetricCard(): void {
    const length = this.getMetricCards().length;
    this.metricCarouselIndex.update((value) => (value + 1) % length);
  }

  prevMetricCard(): void {
    const length = this.getMetricCards().length;
    this.metricCarouselIndex.update((value) => (value - 1 + length) % length);
  }

  getPrevPeriodMetricDataByKey(
    txData: TransactionStats,
    key: MetricKey,
  ): TransactionPreviousPeriod[MetricKey] | undefined {
    const prev = txData.previous_period;
    if (!prev) return undefined;
    return prev[key];
  }

  toCurrencyEntries(currencyMap: Record<string, number> | undefined): Array<{ code: string; amount: number }> {
    if (!currencyMap) return [];
    return Object.entries(currencyMap)
      .map(([code, amount]) => ({ code, amount }))
      .sort((a, b) => b.amount - a.amount);
  }

  /** Success ratio percent for summary progress bar (0–100). */
  getActivitySuccessPercent(summary: TransactionSummary | undefined): number {
    const total = summary?.count ?? 0;
    if (total <= 0) return 0;
    return Math.max(0, Math.min(100, ((summary?.success_count ?? 0) / total) * 100));
  }

  getActivitySuccessLabel(summary: TransactionSummary | undefined): string {
    const s = summary?.success_count ?? 0;
    const t = summary?.count ?? 0;
    return `${s}/${t} successful`;
  }

  /** Two-letter fallback when entity logo is missing. */
  getEntityInitials(item: RecentActivityItem): string {
    const name = item.financial_entity__name?.trim();
    if (name) {
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    }
    const code = item.financial_entity__code?.trim();
    if (code) return code.slice(0, 2).toUpperCase();
    return '—';
  }

  showDirectDebitSeries(): boolean {
    const period = this.selectedPeriod();
    return period === 'today' || period === 'week';
  }

  /**
   * Max wallet_count across currencies for wallet bar scaling.
   */
  getMaxWalletCount(): number {
    const rows = this.walletBalances()?.by_currency ?? [];
    if (!rows.length) return 1;
    return Math.max(...rows.map((r) => r.wallet_count ?? 0), 1);
  }

  /**
   * Percent of wallets with balance vs total wallets.
   */
  getWalletActivePercent(): number {
    const w = this.walletBalances();
    if (!w || w.total_wallets <= 0) return 0;
    return (w.wallets_with_balance / w.total_wallets) * 100;
  }

  /**
   * Get status color class for dash-status.
   */
  getStatusClass(status: string): string {
    const lower = status?.toLowerCase() ?? '';
    if (lower === 'success' || lower === 'completed') return 'success';
    if (lower === 'pending' || lower === 'processing') return 'pending';
    if (lower === 'failed' || lower === 'error') return 'failed';
    return 'default';
  }

  getStatusVariant(status: string): UiBadgeVariant {
    const cls = this.getStatusClass(status);
    if (cls === 'success') return 'success';
    if (cls === 'pending') return 'warning';
    if (cls === 'failed') return 'danger';
    return 'neutral';
  }

  getSuccessPercent(items: StatusBreakdownItem[] | undefined): number {
    const total = this.sumStatusCounts(items);
    if (!total) return 0;
    return Math.max(0, Math.min(100, (this.getSuccessfulCount(items) / total) * 100));
  }

  /**
   * Get the route for a transaction item based on its type.
   */
  getTransactionRoute(item: RecentActivityItem): (string | number | boolean)[] {
    const typeCode = item.type_code ?? 'deposit';
    const routeMap: Record<string, string> = {
      deposit: 'deposits',
      payout: 'payouts',
      refund: 'refunds',
      settlement: 'settlements',
    };
    const entityKey = routeMap[typeCode] ?? 'deposits';
    return this.shellRoutes.commands('manage', entityKey, String(item.id));
  }

  /**
   * Percent change in transaction count vs prior equal-length window (for trend pill).
   */
  getPeriodCountTrendPercent(current: number, previous: number | undefined): number | null {
    if (previous === undefined) return null;
    if (previous === 0) {
      return current === 0 ? 0 : 100;
    }
    return ((current - previous) / previous) * 100;
  }

  /**
   * Format trend for display, e.g. "+12%" or "-3%".
   */
  formatTrendPercent(pct: number | null): string | null {
    if (pct === null) return null;
    const rounded = Math.round(pct * 10) / 10;
    const sign = rounded > 0 ? '+' : '';
    return `${sign}${rounded}%`;
  }

  trendTone(pct: number | null): 'up' | 'down' | 'flat' {
    if (pct === null) return 'flat';
    if (pct > 0) return 'up';
    if (pct < 0) return 'down';
    return 'flat';
  }

  getChartTitle(): string {
    const days = this.getChartDays(this.selectedPeriod());
    if (days === 1) {
      return 'Transaction Activity (Today)';
    }
    return `Transaction Activity (Last ${days} Days)`;
  }

  getPeriodLabel(): string {
    switch (this.selectedPeriod()) {
      case 'today':
        return 'Today';
      case 'week':
        return 'This Week';
      case 'month':
        return 'This Month';
      case 'year':
        return 'This Year';
      case 'quarter':
        return 'Last 3 Months';
      case 'halfyear':
        return 'Last 6 Months';
      default:
        return 'Period';
    }
  }

  getSummarySuccessRatio(summary: TransactionSummary | undefined): string {
    const success = summary?.success_count ?? 0;
    const total = summary?.count ?? 0;
    return `${success}/${total}`;
  }

  private getApiPeriod(period: Period): 'today' | 'week' | 'month' | 'year' | 'custom' {
    if (period === 'quarter' || period === 'halfyear') return 'custom';
    return period;
  }

  private getRollingRange(days: number): [string, string] {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    const toIsoDate = (value: Date): string => value.toISOString().slice(0, 10);
    return [toIsoDate(start), toIsoDate(end)];
  }
}
